import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, ShieldCheck, Lock, CreditCard, FileText, Gift } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-3 pb-10 md:pt-8 md:pb-20 overflow-hidden bg-clinical-base" data-id="element-128">
      <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none" data-id="element-129"></div>
      <div className="absolute top-10 -left-32 w-96 h-96 bg-clinical-softer rounded-full blur-[100px] opacity-50 pointer-events-none" data-id="element-130"></div>

      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-131">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6 lg:gap-12 items-center" data-id="element-132">
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left" data-id="element-133">
            {/* Top Badge */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-clinical-line bg-white mb-4" data-id="element-134">
              <div className="flex items-center gap-1" data-id="element-135">
                <Star size={12} className="fill-clinical-accent text-clinical-accent" data-id="element-136" />
                <span className="text-xs font-semibold text-clinical-deep" data-id="element-137">
                  4.9/5
                </span>
              </div>
              <span className="text-clinical-line" data-id="element-138">·</span>
              <span className="text-xs font-medium text-clinical-ink" data-id="element-139">
                +300 estudiantes
              </span>
            </motion.div>

            {/* Eyebrow */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-mid mb-3" data-id="element-140">
              Atlas Clínico · Enfermería
            </motion.div>

            {/* Headline — ALL CAPS */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="font-heading font-black leading-[0.92] mb-3 md:mb-4 text-[2.25rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] text-clinical-deep uppercase" data-id="element-141">
              MEGA PACK
              <br data-id="element-142" />
              ENFERMERÍA
            </motion.h1>

            {/* Subheadline */}
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-base sm:text-lg md:text-xl text-clinical-mid mb-4 md:mb-5" data-id="element-143">
              Sistema <span className="accent-italic" data-id="element-144">clínico</span> integral
            </motion.h2>

            {/* Promise — with italic gold accents */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-sm sm:text-base md:text-lg text-clinical-ink mb-5 md:mb-6 max-w-xl leading-relaxed" data-id="element-145">
              Aprendé a{' '}
              <span className="accent-italic" data-id="element-146">pensar, valorar y actuar</span>{' '}
              con criterio clínico real para llegar más seguro/a a prácticas,
              internado y hospital.
            </motion.p>

            {/* Mobile Mockup */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.35,
            duration: 0.6
          }} className="lg:hidden relative w-full max-w-[300px] mb-5" data-id="element-147">
              <div className="absolute inset-0 bg-clinical-softer blur-[50px] rounded-full scale-90 pointer-events-none" data-id="element-148"></div>
              <img src="https://cdn.magicpatterns.com/uploads/j6sRYcK5X4hdn124VLpYBV/Copia_de_Diseno_sin_titulo.png" alt="Mega Pack Enfermería" className="relative w-full drop-shadow-[0_20px_40px_rgba(11,42,74,0.25)]" data-id="element-149" />
            </motion.div>

            {/* Microbenefits */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="flex flex-wrap justify-center lg:justify-start items-center gap-2 mb-5 md:mb-7" data-id="element-150">
              {['6 tomos', '6 bonos', 'PDF', 'Acceso inmediato'].map((text, idx) => <div key={idx} className="text-[11px] sm:text-xs text-clinical-deep font-semibold bg-white border border-clinical-line px-3 py-1.5 rounded-full" data-id="element-151">
                    {text}
                  </div>)}
            </motion.div>

            {/* CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="w-full flex flex-col items-center lg:items-start" data-id="element-152">
              <a href="#pricing" className="group inline-flex items-center justify-center gap-2 bg-clinical-cta hover:bg-clinical-ctaHover text-white font-semibold text-sm sm:text-base px-7 sm:px-9 py-4 rounded-full transition-all transform hover:scale-[1.02] shadow-[0_8px_24px_rgba(15,98,254,0.3)] w-full max-w-[360px] sm:w-auto mb-3 uppercase tracking-wide" data-id="element-153">
                Quiero mi Mega Pack
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-154" />
              </a>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-2.5 gap-y-1 text-[10px] sm:text-xs text-clinical-ink/80 font-medium" data-id="element-155">
                <div className="flex items-center gap-1" data-id="element-156">
                  <ShieldCheck size={12} className="text-clinical-mid" data-id="element-157" />
                  <span data-id="element-158">Garantía 7 días</span>
                </div>
                <span className="text-clinical-line" data-id="element-159">·</span>
                <div className="flex items-center gap-1" data-id="element-160">
                  <Lock size={12} className="text-clinical-mid" data-id="element-161" />
                  <span data-id="element-162">Pago seguro</span>
                </div>
                <span className="text-clinical-line" data-id="element-163">·</span>
                <div className="flex items-center gap-1" data-id="element-164">
                  <CreditCard size={12} className="text-clinical-mid" data-id="element-165" />
                  <span data-id="element-166">Mercado Pago</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Desktop Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4,
          duration: 0.8
        }} className="hidden lg:flex relative justify-center lg:justify-end w-full" data-id="element-167">
            <div className="relative w-full max-w-[560px]" data-id="element-168">
              <div className="absolute inset-0 bg-clinical-softer blur-[80px] rounded-full scale-90 pointer-events-none" data-id="element-169"></div>

              <motion.img src="https://cdn.magicpatterns.com/uploads/j6sRYcK5X4hdn124VLpYBV/Copia_de_Diseno_sin_titulo.png" alt="Mega Pack Enfermería" className="relative w-full drop-shadow-[0_25px_50px_rgba(11,42,74,0.25)] animate-float-slow" data-id="element-170" />

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.8
            }} className="absolute top-8 -right-2 bg-white px-4 py-2.5 rounded-2xl shadow-[0_8px_24px_rgba(11,42,74,0.12)] border border-clinical-line flex items-center gap-2.5" data-id="element-171">
                <div className="w-9 h-9 rounded-xl bg-clinical-soft flex items-center justify-center" data-id="element-172">
                  <FileText className="w-4 h-4 text-clinical-mid" data-id="element-173" />
                </div>
                <div data-id="element-174">
                  <p className="text-xs font-bold text-clinical-deep" data-id="element-175">
                    6 Tomos PDF
                  </p>
                  <p className="text-[10px] text-clinical-ink/70" data-id="element-176">
                    Descarga inmediata
                  </p>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1
            }} className="absolute -bottom-2 right-8 bg-white px-4 py-2.5 rounded-2xl shadow-[0_8px_24px_rgba(11,42,74,0.12)] border border-clinical-line flex items-center gap-2.5" data-id="element-177">
                <div className="w-9 h-9 rounded-xl bg-clinical-soft flex items-center justify-center" data-id="element-178">
                  <Gift className="w-4 h-4 text-clinical-mid" data-id="element-179" />
                </div>
                <div data-id="element-180">
                  <p className="text-xs font-bold text-clinical-deep" data-id="element-181">
                    6 Bonos gratis
                  </p>
                  <p className="text-[10px] text-clinical-ink/70" data-id="element-182">
                    Valor $53.000
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}